import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { CountryConfig } from '@app/core/_config/country.config';
import { AuthService } from '@app/core/auth';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { RfqService, QuotesService, RFQ } from '@app/core/quotes';
import { Quotation } from '@app/core/quotes/store/quotes.model';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { PurchaseOrderFormComponent } from '@app/shared/components/purchase-orders/purchase-order-form/purchase-order-form.component';
import { RfqOrderFormComponent } from '@app/shared/components/rfq-order-form/rfq-order-form.component';

import { NegotiatePriceFormComponent } from '../negotiate-price-form/negotiate-price-form.component';
import { QuotationMessagesComponent } from '../quotation-messages/quotation-messages.component';
import { QuotationPopupFormComponent } from '../quotation-popup-form/quotation-popup-form.component';
import { QuotationVariantFormComponent } from '../quotation-variant-form/quotation-variant-form.component';
import { RfqClientProfileComponent } from '../rfq-client-profile/rfq-client-profile.component';
import { RfqDetailsComponent } from '../rfq-details/rfq-details.component';
import { RfqQuotationVariantsComponent } from '../rfq-quotation-variants/rfq-quotation-variants.component';

@Component({
  selector: 'cnc-rfq-details-quotations',
  templateUrl: './rfq-details-quotations.component.html',
  styleUrls: ['./rfq-details-quotations.component.scss'],
})
export class RfqDetailsQuotationsComponent implements OnInit, OnDestroy {
  length = 0;
  pageSize = 20;
  pageSizeOptions :number[] = [5, 10, 25, 100];
  noImgUrl :string = 'no-image-available.png';
  url :string = environment.url;

  rfqSubscription :Subscription;
  qRecord :any = {};

  countries :any = CountryConfig.countries;

  @Input() rfq :RFQ;

  @Input() isQuotationRfq :boolean = false;
  @Output() onSubmitQuotation = new EventEmitter<any>();

  constructor(
    public rfqService :RfqService,
    public quotesService :QuotesService,
    private route :ActivatedRoute,
    public poService :PoService,
    private modalSvc :NgbModal,
    private toastr :ToastrService,
    public settingsSvc :SettingsService,
    public authService :AuthService,
    private cd :ChangeDetectorRef,
    private translate :TranslateService,
  ) {}

  ngOnInit() {
    if (this.isQuotationRfq) {
      this.rfqService.setActive(this.rfq);
    }

    this.quotesService.record$.subscribe((resp) => {
      if (resp && resp.data) {
        this.qRecord = resp;
        this.qRecord.data.forEach((q) => {
          if (q.previous_quotes) {
            try {
              const prevQ :any = JSON.parse(q.previous_quotes);
              prevQ.tiered_pricing_json = JSON.parse(prevQ.tiered_pricing_json);
              q.prevQ = prevQ;
            } catch (e) {}
          }
        });
      }
    });
  }

  ngOnDestroy() {
    this.rfqSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    this.rfqSubscription = this.rfqService.activeRecord$.subscribe((rfq) => {
      if (rfq && rfq.id) {
        this.getRecords();
      }
    });
  }

  getRecords(event ?:PageEvent) {
    let params = { page: 1, per_page: 20 };
    if (event) {
      params = {
        page: event.pageIndex + 1,
        per_page: event.pageSize,
      };
    }

    this.quotesService.setParams(params);
    this.quotesService.refreshList();
  }

  createPurchaseOrder(quotation :any) {
    const modalRef = this.modalSvc.open(PurchaseOrderFormComponent, {
      size: 'lg',
      windowClass: 'po-form-modal',
    });
    modalRef.componentInstance.quotation = quotation;
    modalRef.componentInstance.rfq = this.rfq;
  }

  negotiatePrice(quotation :Quotation) {
    const modalRef = this.modalSvc.open(NegotiatePriceFormComponent, {});
    modalRef.componentInstance.quotation = quotation;
    modalRef.result.then(
      () => {},
      () => {},
    );
  }

  viewClientInfo() {
    // this.rfqService.setActive(rfq, true);
    const modalRef = this.modalSvc.open(RfqClientProfileComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
  }

  showDetails() {
    const modalRef = this.modalSvc.open(RfqDetailsComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.showActions = false;
  }

  showQuotationForm(quotation ?:any) {
    const modalRef = this.modalSvc.open(QuotationPopupFormComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.quotation = JSON.parse(JSON.stringify(quotation));
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      (data) => {
        if (data) {
          this.onSubmitQuotation.emit(data);
          this.cd.detectChanges();
        }
      },
      () => {},
    );
  }

  addQuotation() {
    const modalRef = this.modalSvc.open(QuotationPopupFormComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      (data) => {
        if (data) {
          this.onSubmitQuotation.emit(data);
          this.cd.detectChanges();
        }
      },
      () => {},
    );
  }

  newVariantQuote(quotation :Quotation) {
    const modalRef = this.modalSvc.open(QuotationVariantFormComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.componentInstance.quotation = quotation;
    modalRef.result.then(
      (data) => {
        if (data) {
          this.viewVariantQuotes(quotation);
        }
      },
      () => {},
    );
  }

  viewVariantQuotes(quotation :Quotation) {
    const modalRef = this.modalSvc.open(RfqQuotationVariantsComponent, {
      size: 'lg',
      windowClass: 'xl1280',
    });
    // modalRef.componentInstance.rfq = rfq;
    modalRef.componentInstance.quotation = quotation;
  }

  openMessages(quotation :Quotation) {
    const modalRef = this.modalSvc.open(QuotationMessagesComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.quotation = quotation;
  }

  delete(quotation :Quotation) {
    Swal.fire({
      title: this.translate.instant('MESSAGES.CONFIRM_DELETE_QUOTE', {
        country: quotation.country,
      }),
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.quotesService.delete(quotation.id).subscribe((resp :any) => {
          this.toastr.success(
            this.translate.instant('MESSAGES.DELETE_SUCCESS.TEXT'),
            this.translate.instant('MESSAGES.DELETE_SUCCESS.TITLE'),
          );
        });
      }
    });
  }

  isExpired(date :any) {
    const today = new Date();
    const dt = new Date(date);
    if (today > dt) {
      return true;
    }

    return false;
  }
}
